import { FC, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import * as Sentry from "@sentry/react";
import { ROUTES } from "@/route/routes.constants";
import styles from "./StartInspectionPage.module.scss";
import { systemConfig } from "@/config/systemConfig";
import { API_ENDPOINTS } from "@/api/endpoints.constant";
import { clivApiRequest } from "@/utils/apiClient";
import { Inspection } from "@/types/Inspection.type";
import { loadInspectionForInspectApp } from "@/utils/ensureInspectionHydrated";
import InspectionStatus from "@/enums/InspectionStatus.enum";
import { InspectionImageType } from "@/enums/InspectionImage.enum";
import { RootState } from "@/store/rootReducer";
import { APP_CONFIG_KEY } from "@/types/AppConfig.type";
import { useAppSelector } from "@/store/hooks";
import store from "@/store/store";
import { upsertInspection } from "@/store/slices/inspectionsSlice";
import { clearOpenInspectionLoadError, closeInspection } from "@/store/slices/openInspectionSlice";
import { useNetwork } from "@/hooks/useNetwork";
import { useSavedInspection } from "@/hooks/useSavedInspection";
import { toast } from "react-toastify";
import { getCurrentPosition, watchBestPosition } from "@/utils/geolocation";
import { ExistingOpenInspectionDialog } from "@/features/inspection-list/components/ExistingOpenInspectionDialog";
import { VerificationPhotoDialog, CapturedVerificationImage } from "@/features/inspection-list/components/VerificationPhotoDialog";
import { haversineDistanceMetres } from "@/utils/geoDistance";
import { imageStorage } from "@/store/idbStorage/imageStorage";
import { Image } from "@/types/Image.type";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { ROLE } from "@/role.constant";
import { AppConstants } from "@/app.constants";

type DeviceLocation = {
    latitude: number;
    longitude: number;
    accuracyMetres: number | null;
};

/** Translation keys when inspection is assigned to another user, by status. */
const BLOCK_REASON_BY_STATUS_OTHER_INSPECTOR: Partial<Record<string, string>> = {
    [InspectionStatus.STARTED]: "inspection_already_started_by_other_inspector_error",
    [InspectionStatus.FINISHED]: "inspection_already_finished_by_other_inspector_error",
    [InspectionStatus.CLOSED]: "inspection_already_closed_by_other_inspector_error",
    [InspectionStatus.PAUSED]: "inspection_paused_by_other_inspector_error",
    [InspectionStatus.PLANNED]: "inspection_assigned_to_other_inspector_error",
    [InspectionStatus.CREATED]: "inspection_not_planned_error",
};

/**
 * Returns a translation key if the current user is not allowed to start this inspection; otherwise null.
 */
function getStartBlockReason(
    inspection: Partial<Inspection>,
    currentUserId: string | undefined
): string | null {
    const { inspectionStatusId, inspectedBy } = inspection;

    const isAssignedToOtherUser = inspectedBy && inspectedBy !== currentUserId;

    if (isAssignedToOtherUser && inspectionStatusId) {
        const blockReason = BLOCK_REASON_BY_STATUS_OTHER_INSPECTOR[inspectionStatusId];
        if (blockReason) return blockReason;
    }

    if (inspectionStatusId === InspectionStatus.CREATED) {
        return "inspection_not_planned_error";
    }

    return null;
}

function inspectionRequiresLocationVerification(inspection: Inspection | null): boolean {
    const name = inspection?.controlPoint?.name?.trim();
    if (!name) return false;
    return AppConstants.LOCATION_VERIFICATION_CONTROL_POINT_NAMES.some((n) => n === name);
}

/**
 * Status to set when starting: PLANNED or PAUSED (by same user) → STARTED; otherwise unchanged.
 */
function getStatusForStart(currentStatusId: string): string {
    if (
        currentStatusId === InspectionStatus.PLANNED ||
        currentStatusId === InspectionStatus.PAUSED
    ) {
        return InspectionStatus.STARTED;
    }
    return currentStatusId;
}

/**
 * Precheck page before opening an inspection.
 * Validates that the user can start it, then updates status and navigates to the inspection.
 */
export const StartInspectionPage: FC = () => {
    const { id } = useParams<{ id: string }>();

    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [inspectionDetails, setInspectionDetails] =
        useState<Inspection | null>(null);
    const [deviceLocationReceived, setDeviceLocationReceived] = useState(false);
    const [locationMessageKey, setLocationMessageKey] = useState<string | null>(null);
    const [isRequestingLocation, setIsRequestingLocation] = useState(false);
    const [startUnlockedAfterLocationRetry, setStartUnlockedAfterLocationRetry] = useState(false);
    const [showOpenInspectionDialog, setShowOpenInspectionDialog] = useState(false);
    const [showVerificationDialog, setShowVerificationDialog] = useState(false);
    const [verificationDistanceM, setVerificationDistanceM] = useState<number | null>(null);
    const [deviceLocation, setDeviceLocation] = useState<DeviceLocation | null>(null);
    const [pendingLocationVerified, setPendingLocationVerified] = useState<boolean | null>(null);
    const verificationImagesRef = useRef<Image[]>([]);
    const conflictHandledRef = useRef(false);
    const user = useAppSelector((state: RootState) => state.auth.user);
    const { mandatoryGpsLocation, startInspectionCheck } = useAppSelector(
        (state: RootState) => ({ mandatoryGpsLocation: state.appConfig[APP_CONFIG_KEY.mandatoryGpsLocation], startInspectionCheck: state.appConfig[APP_CONFIG_KEY.startInspectionCheck] })
    );
    const cachedInspection = useAppSelector((state: RootState) => state.inspectionsData?.inspections.find((i: Inspection) => i.id === id)) || null;
    const openInspection = useAppSelector((state: RootState) => state.openedInspection.inspection);
    const { hasRole } = useRoleAccess();
    const isOnline = useNetwork();
    const { savedInspection } = useSavedInspection(id);

    const verificationApplicable = inspectionRequiresLocationVerification(inspectionDetails);
    const mandatoryGpsForThisInspection = Boolean(mandatoryGpsLocation && verificationApplicable);

    const FACTORY_DISTANCE_THRESHOLD_M = 100;
    const MIN_GPS_ACCURACY_METERS = 50;
    const preciseGeoOptions: PositionOptions = {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000,
    };
    const fallbackGeoOptions: PositionOptions = {
        // Allows browser to use Wi‑Fi / cell positioning and cached locations.
        enableHighAccuracy: false,
        maximumAge: 60 * 1000,
        timeout: 8000,
    };

    // Precheck: open-inspection conflict or admin skip. All users land here from card click.
    useEffect(() => {
        if (conflictHandledRef.current) return;
        if (!id) return;
        if (openInspection?.id && openInspection.id !== id) {
            setShowOpenInspectionDialog(true);
            return;
        }
        if (hasRole(ROLE.ADMINISTRATOR)) {
            navigate(ROUTES.INSPECTION_DETAILS.replace(':id', id), {
                state: { skipStartWorkFlow: true },
            });
        }
    }, [id, openInspection?.id, hasRole, navigate]);

    const requestDeviceLocation = async (
        mode: "initial" | "retry" = "initial"
    ): Promise<DeviceLocation | null> => {
        if (!navigator?.geolocation) {
            setDeviceLocationReceived(true);
            setStartUnlockedAfterLocationRetry(true);
            return null;
        }
        setIsRequestingLocation(true);
        setLocationMessageKey(null);

        const applyPosition = (position: GeolocationPosition): DeviceLocation => {
            const { latitude, longitude, accuracy } = position.coords;
            const roundedAccuracy =
                typeof accuracy === "number" && Number.isFinite(accuracy)
                    ? Math.round(accuracy)
                    : null;

            const loc: DeviceLocation = {
                latitude,
                longitude,
                accuracyMetres: roundedAccuracy,
            };

            setDeviceLocation(loc);
            setDeviceLocationReceived(true);
            setLocationMessageKey(null);
            setIsRequestingLocation(false);
            setStartUnlockedAfterLocationRetry(false);

            if (mode === "retry") {
                const registeredLat = inspectionDetails?.factory?.address?.latitude;
                const registeredLon = inspectionDetails?.factory?.address?.longitude;
                const hasRegistered =
                    registeredLat != null && registeredLon != null;
                if (hasRegistered) {
                    const distanceM = haversineDistanceMetres(
                        latitude,
                        longitude,
                        registeredLat,
                        registeredLon
                    );
                    const roundedDistance = Math.round(distanceM);
                    if (roundedDistance > FACTORY_DISTANCE_THRESHOLD_M) {
                        setVerificationDistanceM(distanceM);
                        // setShowVerificationDialog(true); // Disabled: do not open verification dialog.
                    } else {
                        toast.success(
                            t("gps_distance_from_registered_success", {
                                distance: roundedDistance,
                            })
                        );
                    }
                } else {
                    setVerificationDistanceM(null);
                    // setShowVerificationDialog(true); // Disabled: do not open verification dialog.
                }
            }

            return loc;
        };

        const unlockStartWithWarning = () => {
            toast.warn(t("gps_coordinates_not_obtained_start_allowed"));
            setIsRequestingLocation(false);
            setStartUnlockedAfterLocationRetry(true);
            setLocationMessageKey(null);
        };

        try {
            // Keep it snappy: short window on initial load, slightly longer on explicit retry.
            const watchMs = mode === "retry" ? 30_000 : 8_000;
            const bestPrecise = await watchBestPosition({
                maxWatchMs: watchMs,
                stopWhenAccuracyLTE: MIN_GPS_ACCURACY_METERS,
                options: preciseGeoOptions,
            });

            if (bestPrecise) {
                return applyPosition(bestPrecise);
            }

            const fallbackPos = await getCurrentPosition(fallbackGeoOptions);
            return applyPosition(fallbackPos);
        } catch (error: any) {
            if (error?.code === 1) {
                setIsRequestingLocation(false);
                setStartUnlockedAfterLocationRetry(false);
                setLocationMessageKey("enable_location_on_device");
                return null;
            }

            if (mode === "retry") {
                unlockStartWithWarning();
                return null;
            }

            setIsRequestingLocation(false);
            setStartUnlockedAfterLocationRetry(false);
            setLocationMessageKey("enable_location_on_device");
            return null;
        }
    };

    useEffect(() => {
        setStartUnlockedAfterLocationRetry(false);
        setLocationMessageKey(null);

        if (!mandatoryGpsForThisInspection) {
            setDeviceLocationReceived(true);
            return;
        }
        if (!navigator?.geolocation) {
            setDeviceLocationReceived(true);
            return;
        }

        setDeviceLocationReceived(false);

        void requestDeviceLocation("initial");
    }, [mandatoryGpsForThisInspection]);

    const canStart =
        !mandatoryGpsForThisInspection ||
        Boolean(savedInspection) ||
        deviceLocationReceived ||
        startUnlockedAfterLocationRetry;

    const isLocationGateActive =
        mandatoryGpsForThisInspection &&
        !savedInspection &&
        !deviceLocationReceived &&
        !startUnlockedAfterLocationRetry;

    const inspectionUrl = `${systemConfig.apiUrl}/${API_ENDPOINTS.INSPECTION.INSPECTIONS}/${id}`;
    const gotoInspection = (inspection: Inspection, id: string, isLocationVerified?: boolean) => {
        const verificationImages = verificationImagesRef.current;
        const inspectionToSave: Inspection = {
            ...inspection,
            ...(verificationImages.length > 0 ? { verificationImages } : {}),
            ...(typeof isLocationVerified === "boolean" ? { isLocationVerified } : {}),
        };
        store.dispatch(upsertInspection(inspectionToSave));
        navigate(ROUTES.INSPECTION_DETAILS.replace(':id', id), {
            state: { skipStartWorkFlow: true },
        });
    };

    useEffect(() => {
        let cancelled = false;
        const loadInspection = async () => {
            if (!id) {
                setErrorMessage(t("inspection_not_found"));
                return;
            }
            const hydrationErr = (
                location.state as { openInspectionHydrationError?: "offline_lite" | "fetch_failed" } | null
            )?.openInspectionHydrationError;
            if (hydrationErr) {
                store.dispatch(clearOpenInspectionLoadError());
                setErrorMessage(
                    t(
                        hydrationErr === "offline_lite"
                            ? "inspection_hydration_requires_connection"
                            : "inspection_start_failed",
                    ),
                );
                setInspectionDetails(null);
                setIsLoading(false);
                navigate(ROUTES.INSPECTION_START.replace(":id", id), { replace: true, state: {} });
                return;
            }
            setIsLoading(true);
            setErrorMessage(null);
            store.dispatch(clearOpenInspectionLoadError());
            try {
                const result = await loadInspectionForInspectApp({
                    inspectionId: id,
                    isOnline,
                    savedInspection: savedInspection ?? null,
                    listRow: cachedInspection,
                    dispatch: store.dispatch,
                });
                if (cancelled) {
                    return;
                }
                if (!result.ok) {
                    if (result.reason === "offline_lite") {
                        setErrorMessage(t("inspection_hydration_requires_connection"));
                    } else if (result.reason === "not_found") {
                        setErrorMessage(t("inspection_not_found"));
                    } else {
                        setErrorMessage(t("inspection_start_failed"));
                    }
                    setInspectionDetails(null);
                    return;
                }
                setInspectionDetails(result.inspection);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                Sentry.captureException(error);
                setErrorMessage(t("inspection_start_failed"));
                setInspectionDetails(null);
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };
        void loadInspection();
        return () => {
            cancelled = true;
        };
    }, [
        id,
        isOnline,
        savedInspection,
        cachedInspection?.id,
        cachedInspection?.isFullDetailPreloaded,
        cachedInspection?.inspectionStatusId,
        location.state,
        navigate,
        t,
    ]);
    // check if current user can start inspection
    useEffect(() => {
        if (!startInspectionCheck) return;
        if (inspectionDetails) {
            const blockReason = getStartBlockReason(inspectionDetails, user?.id);
            if (blockReason) {
                setErrorMessage(t(blockReason));
            }
        }
    }, [inspectionDetails, user?.id, startInspectionCheck]);

    const startInspection = async (isLocationVerified?: boolean) => {

        // allow to proceed if user is offline for now or inspection is saved on the device(pending upload)
        if (!isOnline || savedInspection) {
            if (!id || !inspectionDetails) {
                return;
            }
            gotoInspection(inspectionDetails, id, isLocationVerified);
            return;
        }
        if (!id || isLoading || !inspectionDetails) return;

        setErrorMessage(null);
        setIsLoading(true);

        let updatedInspection: Inspection | null = null;
        try {

            // update the status and inspected by only if its not assigned to other,
            // allow to reopen inspection by the same user without updating the status
            if (!inspectionDetails.inspectedBy) {
                if (!hasRole(ROLE.ADMINISTRATOR)) {
                    const inspectionStatusId = getStatusForStart(
                        inspectionDetails.inspectionStatusId ?? InspectionStatus.PLANNED
                    );
                    const updateBody: {
                        inspectionStatusId: string;
                        inspectedBy: string | undefined;
                        latitude?: number;
                        longitude?: number;
                    } = {
                        inspectionStatusId,
                        inspectedBy: user?.id,
                    };

                // If inspection has no registered coords yet, set them from device (best effort).
                if (
                    (inspectionDetails.latitude == null || inspectionDetails.longitude == null) &&
                    deviceLocation?.latitude != null &&
                    deviceLocation?.longitude != null
                ) {
                    updateBody.latitude = deviceLocation.latitude;
                    updateBody.longitude = deviceLocation.longitude;
                }

                    const updateResponse = await clivApiRequest<Inspection>({
                        url: inspectionUrl,
                        method: "PUT",
                        body: updateBody,
                    });

                    if (!updateResponse.ok) {
                        setErrorMessage(t("inspection_start_failed"));
                        return;
                    }
                    updatedInspection = { ...inspectionDetails, inspectionStatusId, inspectedBy: user?.id };
                }
            }
            gotoInspection(updatedInspection ?? inspectionDetails, id, isLocationVerified);
        } catch (error) {
            Sentry.captureException(error);
            // setErrorMessage(t("inspection_start_failed")); will handle in phase 2, as we dont want to block offline users for now
            if (updatedInspection) {
                gotoInspection(updatedInspection, id, isLocationVerified);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getRegisteredCoords = (): { lat: number; lng: number } | null => {
        const lat = inspectionDetails?.factory?.address?.latitude;
        const lng = inspectionDetails?.factory?.address?.longitude;
        if (lat == null || lng == null) return null;
        return { lat, lng };
    };

    const computeRegisteredDistance = (loc: DeviceLocation): number | null => {
        const registered = getRegisteredCoords();
        if (!registered) return null;

        return haversineDistanceMetres(
            loc.latitude,
            loc.longitude,
            registered.lat,
            registered.lng
        );
    };

    const computeLocationVerified = (loc: DeviceLocation | null): boolean => {
        const registered = getRegisteredCoords();
        if (!registered) return false;
        if (!loc) return false;
        const distanceM = haversineDistanceMetres(
            loc.latitude,
            loc.longitude,
            registered.lat,
            registered.lng
        );
        return distanceM <= FACTORY_DISTANCE_THRESHOLD_M;
    };

    const handleStart = async () => {
        if (openInspection && openInspection.id && openInspection.id !== id) {
            setShowOpenInspectionDialog(true);
            return;
        }

        if (!inspectionRequiresLocationVerification(inspectionDetails)) {
            await startInspection();
            return;
        }

        const isPlanned = inspectionDetails?.inspectionStatusId === InspectionStatus.PLANNED;
        const registeredAtStart = getRegisteredCoords();
        let locAtStart = deviceLocation ?? null;
        if (isPlanned && !locAtStart) {
            locAtStart = (await requestDeviceLocation("initial")) ?? null;
        }
        const isVerifiedAtStart = computeLocationVerified(locAtStart);
        setPendingLocationVerified(isVerifiedAtStart);

        if (isPlanned) {
            const registered = registeredAtStart;
            const loc = locAtStart;

            // If registered coordinates are missing, still capture device coords (best effort)
            // but require verification before start.
            if (!registered) {
                setVerificationDistanceM(null);
                // setShowVerificationDialog(true); // Disabled: do not open verification dialog.
                await startInspection(isVerifiedAtStart);
                return;
            }

            if (loc) {
                const distance = computeRegisteredDistance(loc);
                if (distance != null && distance > FACTORY_DISTANCE_THRESHOLD_M) {
                    setVerificationDistanceM(distance);
                    // setShowVerificationDialog(true); // Disabled: do not open verification dialog.
                    await startInspection(isVerifiedAtStart);
                    return;
                }
                if (distance != null) {
                    toast.success(
                        t("gps_distance_from_registered_success", {
                            distance: Math.round(distance),
                        })
                    );
                }
            }
        }

        await startInspection(isVerifiedAtStart);
    };

    const handleVerificationConfirm = async (capturedImages: CapturedVerificationImage[]) => {
        if (!id || !inspectionDetails) return;

        const images: Image[] = [];
        for (const img of capturedImages) {
            await imageStorage.setImage(id, InspectionImageType.VERIFICATION, img.id, img.data);
            images.push({
                id: img.id,
                name: img.name,
                type: InspectionImageType.VERIFICATION,
                contentType: img.contentType,
                isNew: true,
            });
        }

        verificationImagesRef.current = images;
        setShowVerificationDialog(false);
        setVerificationDistanceM(null);
        await startInspection(pendingLocationVerified ?? false);
    };

    const handleVerificationCancel = () => {
        setShowVerificationDialog(false);
        setVerificationDistanceM(null);
    };

    const handleResumeExistingInspection = () => {
        conflictHandledRef.current = true;
        setShowOpenInspectionDialog(false);
        if (openInspection?.id) {
            navigate(
                ROUTES.INSPECTION_DETAILS.replace(':id', openInspection.id),
                { state: { skipStartWorkFlow: true }, replace: true }
            );
        }
    };

    const handleConfirmDiscardExistingInspection = async () => {
        conflictHandledRef.current = true;
        if (openInspection) {
            store.dispatch(closeInspection(openInspection.id));
        }
        setShowOpenInspectionDialog(false);
        await startInspection();
    };

    const handleGoBack = () => {
        navigate(ROUTES.INSPECTIONS, {
            replace: true
        });
    };

    if (!id) {
        navigate(ROUTES.INSPECTIONS);
        return null;
    }

    const showError = Boolean(errorMessage);
    const messageText = isLoading
        ? t("loading_inspection_details")
        : t("start_inspection_confirm_message");
    const showInitialLoader =
        isLoading || (mandatoryGpsForThisInspection && isRequestingLocation);

    return (
        <div className={styles.pageContainer}>
            <div className={styles.card}>
                {!showError && (
                    <>
                        <h1 className={styles.title}>
                            {t("start_inspection_confirm_title")}
                        </h1>
                        <p className={styles.message}>{messageText}</p>
                    </>
                )}
                {showError && (
                    <p className={styles.error} role="alert">
                        {errorMessage}
                    </p>
                )}
                {mandatoryGpsForThisInspection && locationMessageKey && (
                    <p className={styles.locationMessage}>
                        {t(locationMessageKey)}
                    </p>
                )}
                {inspectionDetails && (
                    <div className={styles.details}>
                        <p className={styles.detailItem}>
                            <strong>Product:</strong>{" "}
                            {inspectionDetails.product
                                ? `${inspectionDetails.product.name} (${inspectionDetails.product.code})`
                                : "-"}
                        </p>
                        <p className={styles.detailItem}>
                            <strong>Internal order ref:</strong>{" "}
                            {inspectionDetails.internalOrderReference}
                        </p>
                        {inspectionDetails.externalOrderReference && <p className={styles.detailItem}>
                            <strong>External order ref:</strong>{" "}
                            {inspectionDetails.externalOrderReference}
                        </p>}
                    </div>
                )}
                {showInitialLoader && (
                    <div className={styles.loaderContainer}>
                        <CircularProgress size={24} />
                    </div>
                )}
                <div className={styles.actions}>
                    <button
                        type="button"
                        onClick={handleGoBack}
                        className={styles.cancelButton}
                        disabled={isLoading}
                    >
                        {t("Back")}
                    </button>
                    {!showError && (
                        <>
                            {isLocationGateActive && Boolean(locationMessageKey) && (
                                <button
                                    type="button"
                                    onClick={() => void requestDeviceLocation("retry")}
                                    className={styles.startButton}
                                    disabled={isRequestingLocation}
                                >
                                    {isRequestingLocation
                                        ? t("Loading")
                                        : t("retry")}
                                </button>
                            )}
                            {!isLocationGateActive && (
                                <button
                                    type="button"
                                    onClick={handleStart}
                                    className={styles.startButton}
                                    disabled={isLoading || !canStart}
                                >
                                    {isLoading
                                        ? t("Loading")
                                        : t("start_inspection_confirm_button")}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
            {showOpenInspectionDialog && openInspection && (
                <ExistingOpenInspectionDialog
                    openInspection={openInspection}
                    onResume={handleResumeExistingInspection}
                    onDiscardConfirm={handleConfirmDiscardExistingInspection}
                    onCancelDiscard={() => setShowOpenInspectionDialog(false)}
                />
            )}
            {showVerificationDialog && (
                <VerificationPhotoDialog
                    distanceMetres={verificationDistanceM}
                    registeredCoords={
                        inspectionDetails?.factory?.address?.latitude != null &&
                            inspectionDetails?.factory?.address?.longitude != null
                            ? {
                                lat: inspectionDetails.factory.address.latitude,
                                lng: inspectionDetails.factory.address.longitude,
                            }
                            : null
                    }
                    currentCoords={
                        deviceLocation?.latitude != null && deviceLocation?.longitude != null
                            ? { lat: deviceLocation.latitude, lng: deviceLocation.longitude }
                            : null
                    }
                    currentAccuracyMetres={deviceLocation?.accuracyMetres ?? null}
                    onConfirm={handleVerificationConfirm}
                    onCancel={handleVerificationCancel}
                />
            )}
        </div>
    );
};


